import React, { useState } from 'react';
import { Sparkles, Shield, TrendingUp, Target, BarChart3, LogIn } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Login() {
  const [loginMethod, setLoginMethod] = useState('google'); // 'google' or 'email'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        navigate('/dashboard', { state: { user: response.data.user } });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login failed';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex',
      background: 'linear-gradient(135deg, hsl(210, 75%, 55%) 0%, hsl(180, 52%, 48%) 100%)'
    }}>
      {/* Left Side */}
      <div style={{ flex: 1, padding: '80px 60px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ maxWidth: '500px' }}>
          <h1 style={{ fontSize: '3.5rem', fontWeight: '700', marginBottom: '24px', lineHeight: '1.1' }}>Clariti</h1>
          <p style={{ fontSize: '1.5rem', marginBottom: '32px', opacity: 0.95 }}>AI-Enabled Marketing Effectiveness & Spend Intelligence</p>
          <p style={{ fontSize: '1.125rem', lineHeight: '1.7', opacity: 0.9, marginBottom: '48px' }}>
            Multi-agent AI platform for explainable budget optimization, real-time competitive intelligence, and data-driven marketing decisions.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              { icon: Sparkles, text: '6 Specialized AI Agents with LangGraph Orchestration' },
              { icon: TrendingUp, text: 'Monte Carlo Simulations & Scenario Modeling' },
              { icon: Target, text: 'Real-Time Competitive Intelligence (Tavily)' },
              { icon: BarChart3, text: 'Time Machine & Knowledge Graph Analytics' }
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={24} />
                  </div>
                  <p style={{ fontSize: '1rem', lineHeight: '1.4' }}>{feature.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div style={{ width: '500px', background: 'white', padding: '80px 60px', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: '-10px 0 40px rgba(0,0,0,0.1)' }}>
        <div>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{ width: '80px', height: '80px', margin: '0 auto 24px', borderRadius: '20px', background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <Shield size={40} />
            </div>
            <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>Welcome to Clariti</h2>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>Sign in to access the platform</p>
          </div>

          {/* Login Method Toggle */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', padding: '4px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
            <button
              onClick={() => setLoginMethod('google')}
              style={{
                flex: 1,
                padding: '10px',
                border: 'none',
                borderRadius: '8px',
                background: loginMethod === 'google' ? 'white' : 'transparent',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Google OAuth
            </button>
            <button
              onClick={() => setLoginMethod('email')}
              style={{
                flex: 1,
                padding: '10px',
                border: 'none',
                borderRadius: '8px',
                background: loginMethod === 'email' ? 'white' : 'transparent',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Email & Password
            </button>
          </div>

          {loginMethod === 'google' ? (
            <div>
              <button
                onClick={handleGoogleLogin}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  border: '2px solid var(--border-medium)',
                  borderRadius: '12px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                data-testid="google-signin-btn"
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              
              <div style={{ marginTop: '24px', padding: '16px', background: 'hsl(199, 89%, 96%)', border: '1px solid var(--info-500)', borderRadius: '12px' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--info-700)', lineHeight: '1.6' }}>
                  ℹ️ New Google OAuth users: First 2 users get automatic admin access. Users 3+ require admin approval.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <form onSubmit={handleEmailLogin}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px', color: 'var(--text-secondary)' }}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your.email@company.com"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid var(--border-light)',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                    data-testid="email-input"
                  />
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px', color: 'var(--text-secondary)' }}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid var(--border-light)',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                    data-testid="password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                  style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: '700', justifyContent: 'center' }}
                  data-testid="email-login-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></div>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn size={20} />
                      Sign In
                    </>
                  )}
                </button>
              </form>

              <div style={{ marginTop: '24px', padding: '16px', background: 'hsl(38, 92%, 95%)', border: '1px solid var(--warning-500)', borderRadius: '12px' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--warning-700)', lineHeight: '1.6' }}>
                  🔑 Credentials provided by admin with temporary password. You'll be prompted to change it on first login.
                </p>
              </div>
            </div>
          )}

          {/* Quick Admin Login */}
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--border-light)' }}>
            <button
              onClick={() => {
                setLoginMethod('email');
                setEmail('demo@clariti.com');
                setPassword('DemoAdmin2024!');
                // Auto-submit after a brief moment to show credentials
                setTimeout(() => {
                  const form = document.querySelector('form');
                  if (form) form.requestSubmit();
                }, 100);
              }}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed var(--accent-500)',
                borderRadius: '10px',
                background: 'hsl(180, 65%, 97%)',
                color: 'var(--accent-700)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'hsl(180, 65%, 94%)';
                e.currentTarget.style.borderColor = 'var(--accent-600)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'hsl(180, 65%, 97%)';
                e.currentTarget.style.borderColor = 'var(--accent-500)';
              }}
              data-testid="quick-admin-login"
            >
              🚀 Quick Admin Login (Demo Account)
            </button>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: '8px' }}>
              Click to auto-fill demo admin credentials
            </p>
          </div>

          <p style={{ marginTop: '48px', fontSize: '0.875rem', color: 'var(--text-tertiary)', textAlign: 'center' }}>
            Enterprise-Grade AI Marketing Platform
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
